import { QueryCache, QueryClient } from '@tanstack/react-query';
import { CACHE_TIME } from '@/config/constants';
import { isAxiosError } from '@/lib/axios';
import { toaster } from '@/components/ui/lib/toast-utils';
import { DICTIONARY } from '@/config/dictionary';
import { getApiErrorMessage } from '@/utils/api-error';

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // 401s trigger a sign-in redirect via the axios interceptor's `auth:expired`
      // event — a toast here would just flash before the redirect happens.
      if (isAxiosError(error) && error.response?.status === 401) return;
      // If the query already has data (a background refetch failure), keep showing
      // the stale data quietly rather than interrupting with a toast.
      if (query.state.data !== undefined) return;

      toaster.create({
        type: 'error',
        title: DICTIONARY.common.toast.loadError,
        description: getApiErrorMessage(error),
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: CACHE_TIME.stale,
      gcTime: CACHE_TIME.gc,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (
          error instanceof Error &&
          'status' in error &&
          typeof (error as { status?: number }).status === 'number'
        ) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
});
